import React, { useEffect, useState } from 'react';
import Modal, { FullScreen } from '../../../../global/components/modal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import SortableList from '../../../../global/components/sortable-list';
import { Row, Column } from '../../../../global/components/row';
import { size, get, first, set, orderBy, last } from '../../../../global/lib/lodash';
import { Button, IconButton } from '../../../../global/components/Button';
import { Text } from '../../../../global/components/text';
import { useStyles } from './style';
import { RoomSelect } from '../../../../global/components/room-select';
import { CheckpointScore } from '../../../report/ChecklistScores';
import { CenterLoader } from '../../../../global/styles';
import { Icon } from '@mui/material';
import colors from '../../../../global/styles/colors';
import SnackAlerts from '../../../../global/components/snack-alerts';
import sendData from '../../../../redux/actions/sendData';
import { convertToPostStructure } from '../../../../global/utils';
import { useWindowSize } from '../../../../global/hooks/useWindowSize';
import { useIntersect } from '../../../../global/hooks/useIntersect';
import ScoreTag from '../../../report/AssessmentScore/ObservationCreate/ScoreTag';
import Confirm from '../../../../global/components/confirm';

const INCLUDES = [
  'realm',
  'checklists',
  'checklists.images',
  'checklists.checkpoints',
  'checklists.checkpoints.images',
  'checklists.checkpoints.checkpointOptions',
  'checklists.checkpoints.scorecardOptions',
  'checklists.classification',
  'checklists.classification.assembly',
  'checklists.classification.material',
  'checklists.classification.system',
  'checklists.tradeTypes',
  'checklists.parent',
  'edition',
]

const Checklist = props => {
  const { supersets, checklist, updateChecks, copiedRooms, onCopy, isMobileXS, rooms } = props;
  const { checkpoints } = checklist;

  const styles = useStyles();

  const [ref, view] = useIntersect({ rootMargin: '0px' });

  const fixMargins = { marginLeft: 5, pointerEvents: 'none' };

  const getSupersetName = () => {
    if (!size(supersets)) { return '' }
    const sup = supersets.find(s => s.id == checklist.supersetId);
    if (sup) {
      return `${sup.name}: `
    } else {
      return ''
    }
  }

  const title = (style = { minWidth: 200 }) => {
    return (
      <Row style={style}>
        <Text bold type="title_medium">{`${getSupersetName()}${checklist.name} (${size(checkpoints)})`}</Text>
      </Row>
    )
  };

  const CONT = isMobileXS ? Column : Row;

  const renderCP = (cp, _i) => {
    const isOpen = true;

    const renderCheckpointScore = (_cp, style) => {
      return <CheckpointScore checkpoint={{ ..._cp, criteria: _cp.description }} checklist={checklist} showDrop={false} showContent={!!style.cursor && !!isOpen} showDisplayScore={false} />
    }

    const cpTitle = (style = { cursor: 'pointer', marginLeft: -20, minWidth: 200, maxWidth: isMobileXS ? undefined : '50%' }) => {
      return (
        <Row style={style}>
          {renderCheckpointScore(cp, style)}
        </Row>
      )
    };

    return (
      <div key={_i} style={{ marginBottom: isMobileXS ? 10 : 0 }}>
        <CONT>
          {cpTitle()}
          <div style={{ marginLeft: 'auto' }}>
            <RoomSelect type="inlineModal" onCopy={onCopy} onPaste={copiedRooms ? () => copiedRooms : undefined} value={cp.rooms} compare={checklist.rooms} rooms={rooms} onChange={(s, areEqual) => updateChecks(checklist, s, cp, areEqual)} modalTitle={cpTitle(fixMargins)} highlightOriginals />
          </div>
        </CONT>
      </div>
    )
  }

  return (
    <div ref={ref}>
      <CONT className={styles.checklistHeader}>
        {title()}
        <div style={{ marginLeft: 'auto' }}>
          <RoomSelect rooms={rooms} onCopy={onCopy} onPaste={copiedRooms ? () => copiedRooms : undefined} onChange={(s, areEqual) => updateChecks(checklist, s, null, areEqual)} type="inlineModal" modalTitle={title({ ...fixMargins, marginLeft: 30, marginTop: 4 })} />
        </div>
      </CONT>

      {view.isIntersecting && checkpoints.map(renderCP)}
    </div>
  )
}

export const RoomAssignments = props => {
  const { scorecard } = props;
  const [rooms, setRooms] = useState([
    { key: '0', title: 'Enabled', items: [], canDrag: false, },
    { key: '1', title: 'Disabled', items: [], canDrag: false, },
  ]);
  const [checklists, setChecklists] = useState([]);
  const [state, setState] = useState({
    copiedRooms: null,
    loading: false,
  })
  const [alerts, setAlerts] = useState();
  const [preview, setPreview] = useState({
    open: false,
    showSearch: false,
    onClose: () => {
      setPreview(p => ({ ...p, open: false }))
    }
  })

  const styles = useStyles();
  
  const { isMobileXS } = useWindowSize();

  const roomData = useRetrieveData({ url: '/realms' }, {
    build: ['realms'],
    params: {
      filter: {
        tier: 'room'
      }
    }
  })

  const scdata = useRetrieveData({ url: `/scorecards/${scorecard.id}` }, {
    build: ['scorecards'],
    params: {
      include: INCLUDES.join(','),
    },
    transformData: t => {
      const card = first(t);
      const clists = get(card, 'checklists', []).map(cl => {
        const { checkpoints } = cl;
        return {
          ...cl,
          checkpoints: orderBy(checkpoints, 'name'),
        } 
      })
      return {
        ...card,
        checklists: clists,
      }
    },
  }) 

  const supersets = useRetrieveData({ url: '/supersets' }, {
    build: ['supersets'],
  })

  const isLoading = roomData.loading || scdata.loading;

  const renderRoomCat = r => {
    return (
      <div style={{ paddingTop: 20 }}>
        <Text bold>{r.title}</Text>
        {isLoading && <CenterLoader />}
        {!size(r.items) && !isLoading && r.key == '0' && <div>No rooms enabled; drag and drop rooms here to get started</div>}
      </div>
    )
  }

  useEffect(() => {
    setChecklists(get(scdata, 'data.checklists', []));
  }, [scdata.data])

  useEffect(() => {
    if (size(roomData.data) && size(checklists)) {
      const roomCheckpoints = get(scdata, 'data.config.roomCheckpoints', []);
      const newcls = [...checklists];

      const enabled = {
        ...first(rooms),
        items: [],
      }

      const disabled = {
        ...last(rooms),
        items: [],
      }

      roomData.data.map(room => {
        const rcpi = roomCheckpoints.findIndex(r => r.id == room.id)
        const rcp = roomCheckpoints[rcpi];

        if (!rcp) {
          return disabled.items.push(room);
        }

        if (size(rcp.cps)) {
          newcls.forEach(cl => {
            const { checkpoints } = cl;
            
            checkpoints.forEach(cp => {
              if (rcp.cps.includes(cp.id)) {
                set(cp, 'rooms', [...get(cp, 'rooms', []), room]);
              }
            })
          })
        }

        if (size(rcp.cps) || rcp.act) {
          enabled.items.push({ ...room, index: rcpi });
        } else {
          disabled.items.push(room)
        }
      });

      enabled.items = orderBy(enabled.items, 'index')

      setChecklists(newcls);
      setRooms([enabled, disabled]);
    }
  }, [size(roomData.data), size(checklists)]);

  const findRoomInCheckpoints = (room) => {
    let count = 0;
    checklists.map(cl => {
      const { checkpoints } = cl;
      checkpoints.map(cp => {
        get(cp, 'rooms', []).map(r => {
          if (r.id == room.id) {
            count++;
          }
        })
      })
    })
    return count;
  }

  const onChange = () => {
    props.onChange && props.onChange();
  }

  const updateChecks = (checklist, sel, cp, areEqual) => {
    if (!cp) {
      checklist.rooms = sel;
    }
    setChecklists(clss => {
      return clss.map(cls => {
        if (cls.id === checklist.id) {
          return {
            ...cls,
            checkpoints: cls.checkpoints.map(c => {
              const setRooms = () => {
                c.rooms = sel;
              }
  
              if (cp) {
                if (c.id == cp.id) {
                  setRooms();
                }
              } else {
                setRooms();
              }
              
              return c;
            })
          }
        } else {
          return cls;
        }
      })
    })

    if (!areEqual) {
      onChange();
    }
  }

  const onCopy = (rms) => {
    setState(s => ({ ...s, copiedRooms: rms }));
    setAlerts({ 
      message: () => <div>Rooms copied to clipboard!<br />Use <Icon>post_add_oulined</Icon> to paste to another category</div> 
    })
  }

  const cancel = () => {
    props.onCancel && props.onCancel();
  }

  const getCurrentRoomCheckpoints = () => {
    const rcps = [];

    rooms.map(group => {
      const { items, key } = group;

      items.map(rm => {
        const cps = [];

        checklists.map(cl => {
          const { checkpoints } = cl;
          checkpoints.map(cp => {
            get(cp, 'rooms', []).map(r => {
              if (rm.id == r.id) {
                cps.push(cp.id);
              }
            })
          })
        })

        rcps.push({ id: rm.id, cps, act: key == '0' });
      })
    });

    return rcps;
  }

  const previewClick = room => {
    const sc = get(scdata, 'data', {});
    const allScoreData = [
      { depth: 0, id: get(sc, 'id'), name: get(sc, 'name'), order: 0, scorecardId: get(sc, 'id') },
    ];

    supersets.data.map(s => {
      allScoreData.push({ id: s.id, depth: 1, parentId: get(sc, 'id'), scorecardId: sc.id, supersetId: s.id, name: s.name })
    })

    checklists.map(cl => {
      const sup = supersets.data.find(s => s.id == cl.supersetId);
      allScoreData.push({ id: cl.id, depth: 2, parentId: sup.id, scorecardId: sc.id, name: cl.name })
      cl.checkpoints.map(cp => {
        allScoreData.push({ id: cp.id, depth: 3, parentId: cl.id, scorecardId: sc.id, checkpointId: cp.id, name: cp.name, criteria: cp.criteria || cp.description }) 
      })
    })

    setPreview(p => ({
      ...p,
      room,
      open: true,
      subTitle: `${room.name} Criteria Preview`,
      allScoreData,
      scorecard: {
        ...sc,
        config: {
          ...sc.config,
          roomCheckpoints: getCurrentRoomCheckpoints(),
        }
      }
    }))
  }

  const renderRoom = (r, group) => {
    const count = findRoomInCheckpoints(r);
    const isDisabled = group.key != '0';
    const color = isDisabled ? colors.black : colors.blue;
    const previewDisabled = count == 0 || isDisabled;
    return (
      <div style={{ position: 'relative', paddingTop: 14 }}>
        <Row style={{ opacity: isDisabled ? 0.5 : 1 }}>
          <div>{r.name}</div>
          <Row style={{ marginLeft: 'auto', color }}>
            <Text italic>{`${count} Checkpoints`}</Text>
            <IconButton 
              disabled={previewDisabled} 
              onClick={() => previewClick(r)} 
              size="small" 
              style={{ 
                color: previewDisabled ? colors.black : colors.blue,
                opacity: !isDisabled && previewDisabled ? 0.5 : 1
              }} 
              variant="none" 
              icon="visibility" 
            />
          </Row>
        </Row>
      </div>
    )
  }

  const submit = async () => {
    try {
      setState(s => ({ ...s, loading: true }))
      const room_checkpoints = getCurrentRoomCheckpoints();

      const config = get(scdata, 'data.config', {});

      const data = {
        data: {
          id: scorecard.id,
          type: 'scorecards',
          attributes: {
            config: {
              ...convertToPostStructure(config),
              room_checkpoints,
            }
          }
        }
      }

      await sendData({
        url: `/scorecards/${scorecard.id}`,
        method: 'patch',
        data,
      }, { simulate: false })

      props.onClose && props.onClose(true);
    } catch (err) {
      console.warn(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState(s => ({ ...s, loading: false }))
    }
  }

  const enabledRooms = roomData.data.filter(r => get(rooms, '0.items', []).find(i => i.id == r.id))

  return (
    <div className={styles.container}>
      <section className={styles.section}>
        <Text type="headline_large" bold>Room-Assignments</Text>
        <Text type="label_large"><b>{get(props, 'subtitleAdd', '')}</b>{'Room assignments are only applicable to Lot-level Assessments. Not all rooms need to be used.'}</Text>
      </section>

      <section className={styles.section}>
        <Text type="headline_large" bold>Room Order</Text>
        <SortableList items={rooms.map(r => ({ ...r, content: () => renderRoomCat(r), items: r.items.map(room => ({ ...room, key: `${room.id}`, content: () => renderRoom(room, r) })) }))} verticalOnly onSort={r => { setRooms(r); onChange(); }} />
      </section>

      <section className={styles.section}>
        <Text type="headline_large" bold>Criteria Assignment</Text>
        {scdata.loading && <CenterLoader />}
        {checklists.map((c, i) => <Checklist supersets={supersets.data} checklist={c} key={i} updateChecks={updateChecks} onCopy={onCopy} isMobileXS={isMobileXS} rooms={enabledRooms} {...state} />)}
      </section>

      <Row center style={{ position: 'fixed', bottom: 10, left: 0, right: 0 }}>
        <Button title="Cancel" bgColor={colors.danger} icon="close" onClick={cancel} />
        <Button title="Save" bgColor={colors.blue} loading={state.loading} icon="save" onClick={submit} style={{ marginLeft: 10 }} />
      </Row>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />

      <ScoreTag {...preview} />
    </div>
  )
}

export const RoomAssignmentsModal = props => {
  const [state, setState] = useState({
    hasChange: false,
  })
  const [conf, setconf] = useState();

  const { modalProps } = props;

  const closeModal = () => {
    modalProps.onClose();
    setState(s => ({ ...s, hasChange: false }));
  }

  const onClose = (saved) => {
    if (state.hasChange && !saved) {
      setconf({
        title: 'Unsaved changes',
        content: 'You have unsaved data, are you sure you want to close?',
        onOk: () => {
          setconf()
          closeModal()
        },
        onCancel: () => {
          setconf()
        }
      })
    } else {
      closeModal();
    }
  }

  return (
    <>
      <FullScreen {...modalProps} onClose={onClose}>
        <RoomAssignments {...props} onChange={() => setState(s => ({ ...s, hasChange: true }))} onClose={onClose} onCancel={onClose} />
      </FullScreen>
      <Confirm {...conf} />
    </>
  )
}